import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import { BarChart3, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

const SESSION_TYPE_COLORS = {
  training: "#B45309", // amber-700
  match: "#4F46E5", // indigo-600
  recovery: "#0F766E", // teal-700
};

// Session types with a translated label under team.sessionTypes.*
const SESSION_TYPE_LABELS = new Set(["training", "match", "recovery"]);

const SESSION_TYPE_BG_COLORS = {
  training: "#FEF3C7", // amber-100
  match: "#E0E7FF", // indigo-100
  recovery: "#CCFBF1", // teal-100
};

// Fixed bar heights (% of plot area) for the loading skeleton — deterministic
// so the shimmer doesn't jitter between renders.
const SKELETON_BAR_HEIGHTS = [58, 82, 45, 90, 66, 52, 74, 61, 88, 48];

const TeamLoadChart = ({
  data = [],
  dataKey = "teamLoad",
  loading = false,
  comparisonMode = false,
  playerName = "",
  peerAvgTraining = null,
  peerAvgMatch = null,
  sessionType = "all",
}) => {
  const { t } = useTranslation();
  const typeLabel = (type) =>
    SESSION_TYPE_LABELS.has(type) ? t(`team.sessionTypes.${type}`) : undefined;
  const chartTitle = comparisonMode
    ? t("team.loadChart.comparisonTitle")
    : t("team.loadChart.teamTitle");
  const chartSubtitle = comparisonMode
    ? t("team.loadChart.comparisonSubtitle", { name: playerName })
    : t("team.loadChart.teamSubtitle");
  // Get unique session types from data for legend
  const sessionTypes = [...new Set((data || []).map((d) => d.type))];

  // Track which types are hidden
  const [hiddenTypes, setHiddenTypes] = useState(new Set());

  // Calculate peer averages from data if not provided
  const calculatedPeerAvgs = useMemo(() => {
    if (peerAvgTraining !== null && peerAvgMatch !== null) {
      return { training: peerAvgTraining, match: peerAvgMatch };
    }
    if (!comparisonMode || !data || !data.length)
      return { training: 0, match: 0 };

    // Calculate average from peerAvg values in data by session type
    const trainingData = (data || []).filter(
      (d) => d.type === "training" && d.peerAvg
    );
    const matchData = (data || []).filter(
      (d) => d.type === "match" && d.peerAvg
    );

    const trainingAvg = trainingData.length
      ? Math.round(
          trainingData.reduce((sum, d) => sum + d.peerAvg, 0) /
            trainingData.length
        )
      : 0;
    const matchAvg = matchData.length
      ? Math.round(
          matchData.reduce((sum, d) => sum + d.peerAvg, 0) / matchData.length
        )
      : 0;

    return { training: trainingAvg, match: matchAvg };
  }, [data, comparisonMode, peerAvgTraining, peerAvgMatch]);

  // Toggle type visibility
  const toggleType = (type) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  // Filter data based on hidden types
  const filteredData = (data || []).filter((d) => !hiddenTypes.has(d.type));

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const sessionName = payload[0]?.payload?.sessionName;
      const sessionType = payload[0]?.payload?.type;

      // In comparison mode, show player value and comparison to peer avg
      if (comparisonMode) {
        const playerLoad = payload.find((p) => p.dataKey === dataKey)?.value;
        const peerAvgValue =
          sessionType === "match"
            ? calculatedPeerAvgs.match
            : calculatedPeerAvgs.training;

        // Calculate difference from peer
        const diff = playerLoad - peerAvgValue;
        const diffPercent =
          peerAvgValue > 0 ? Math.round((diff / peerAvgValue) * 100) : 0;
        const isAbove = diff > 0;
        const isNeutral = Math.abs(diffPercent) < 3;

        return (
          <div className="min-w-[160px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
            <p className="mb-1 text-[13px] font-semibold text-[#111827]">
              {sessionName}
            </p>
            <p className="mb-2 text-[11px] text-[#9CA3AF]">{label}</p>
            <div className="space-y-2 border-t border-[#F3F4F6] pt-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded bg-[#E07A3A]" />
                  <span className="text-[11px] text-[#6B7280]">
                    {playerName}
                  </span>
                </div>
                <span className="text-sm font-semibold text-[#111827]">
                  {playerLoad?.toLocaleString()}
                  <span className="ml-0.5 text-xs font-normal text-[#6B7280]">
                    g
                  </span>
                </span>
              </div>
              {/* Peer comparison badge */}
              <div
                className="flex items-center justify-between rounded-md px-2 py-1.5"
                style={{
                  backgroundColor: isNeutral
                    ? "#F3F4F6"
                    : isAbove
                    ? "#FEF3E7"
                    : "#F0FDF4",
                }}
              >
                <span className="text-[11px] text-[#6B7280]">
                  {t("team.loadChart.vsPeerAvg")}
                </span>
                <div className="flex items-center gap-1">
                  {isNeutral ? (
                    <span className="text-[11px] font-medium text-[#6B7280]">
                      {t("team.loadChart.onTrack")}
                    </span>
                  ) : (
                    <>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        style={{
                          transform: isAbove
                            ? "rotate(0deg)"
                            : "rotate(180deg)",
                        }}
                      >
                        <path
                          d="M6 2.5L9.5 6.5H2.5L6 2.5Z"
                          fill={isAbove ? "#E07A3A" : "#10B981"}
                        />
                      </svg>
                      <span
                        className="text-[11px] font-semibold"
                        style={{ color: isAbove ? "#E07A3A" : "#10B981" }}
                      >
                        {isAbove
                          ? t("team.loadChart.aboveBy", {
                              value: Math.abs(diff).toLocaleString(),
                            })
                          : t("team.loadChart.belowBy", {
                              value: Math.abs(diff).toLocaleString(),
                            })}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      const loadValue = payload[0]?.value;
      return (
        <div className="min-w-[140px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <p className="text-[13px] font-semibold text-[#111827]">
              {sessionName}
            </p>
            <span
              className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{
                backgroundColor: SESSION_TYPE_BG_COLORS[sessionType],
                color: SESSION_TYPE_COLORS[sessionType],
              }}
            >
              {typeLabel(sessionType)}
            </span>
          </div>
          <p className="mb-2 text-[11px] text-[#9CA3AF]">{label}</p>
          <div className="border-t border-[#F3F4F6] pt-2">
            <p className="mb-0.5 text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {t("team.loadChart.totalLoad")}
            </p>
            <p className="text-lg font-bold text-[#111827]">
              {loadValue.toLocaleString()}
              <span className="ml-0.5 text-xs font-normal text-[#6B7280]">
                g
              </span>
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Empty state component
  const EmptyState = () => (
    <div className="flex h-[240px] flex-col items-center justify-center px-4 text-center">
      {/* Illustration */}
      <div className="relative mb-4">
        {/* Background glow */}
        <div
          className="absolute inset-0 left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(232, 133, 74, 0.12) 0%, rgba(232, 133, 74, 0) 70%)",
          }}
        />
        {/* Icon container */}
        <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
          <BarChart3 size={28} className="text-[#E07A3A]" />
        </div>
        {/* Phone decoration */}
        <div className="absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white shadow-sm">
          <Smartphone size={12} className="text-[#9CA3AF]" />
        </div>
      </div>

      {/* Text */}
      <h4 className="mb-1 text-sm font-semibold text-[#111827]">
        {t("team.loadChart.emptyTitle")}
      </h4>
      <p className="max-w-[240px] text-xs leading-relaxed text-[#6B7280]">
        {t("team.loadChart.emptyBody")}
      </p>
    </div>
  );

  // Distinct state for when sessions DO exist in the period but every one has
  // zero load — a flat all-zero chart looks broken. Ghost bars (one per
  // session, sitting on a baseline) make it clear the sessions are there, just
  // empty, rather than pretending there's nothing at all.
  const ZeroLoadState = ({ count }) => {
    const bodyKey =
      sessionType === "training"
        ? "team.loadChart.zeroLoad.bodyTraining"
        : sessionType === "match"
        ? "team.loadChart.zeroLoad.bodyMatch"
        : "team.loadChart.zeroLoad.bodyAll";
    const bars = Math.min(Math.max(count, 1), 9);
    return (
      <div className="flex h-[240px] flex-col items-center justify-center px-4 text-center">
        {/* Ghost bars on a dashed baseline — sessions present, all zero */}
        <div
          className="mb-5 flex items-end justify-center gap-2 border-b-2 border-dashed border-[#EAECEF] pb-px"
          aria-hidden="true"
        >
          {Array.from({ length: bars }).map((_, i) => (
            <div key={i} className="h-2.5 w-7 rounded-t-md bg-[#F1F0EE]" />
          ))}
        </div>

        <h4 className="mb-1 text-sm font-semibold text-[#111827]">
          {t("team.loadChart.zeroLoad.title")}
        </h4>
        <p className="mb-3 max-w-[290px] text-xs leading-relaxed text-[#6B7280]">
          {t(bodyKey, { count })}
        </p>

        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F3F4F6] px-2.5 py-1 text-[11px] font-medium text-[#6B7280]">
          <span className="h-1.5 w-1.5 rounded-full bg-[#D1D5DB]" />
          {t("team.loadChart.zeroLoad.badge", { count })}
        </span>
      </div>
    );
  };

  // Loading state (e.g. switching between players). Mirrors the real chart's
  // structure exactly — same title block, same right-aligned legend row, same
  // h-[200px] plot area with a y-axis column, bars on a baseline, and x-axis
  // labels — so it shimmers in place instead of flashing a different layout.
  if (loading) {
    return (
      <div>
        {/* Title — identical block to the loaded chart (static text) */}
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-[#111827]">{chartTitle}</h3>
          <p className="text-xs text-[#6B7280]">{chartSubtitle}</p>
        </div>

        {/* Legend row — same mb-4, right-aligned, so nothing shifts */}
        <div className="mb-4 flex items-center justify-end gap-4">
          <div className="shimmer h-2.5 w-14 rounded" />
          <div className="shimmer h-2.5 w-14 rounded" />
        </div>

        {/* Plot area — same h-[200px] footprint as the real chart */}
        <div className="flex h-[200px] flex-col">
          <div className="flex min-h-0 flex-1 gap-2">
            {/* y-axis ticks */}
            <div className="flex w-5 flex-col justify-between py-0.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="shimmer h-1.5 w-3.5 rounded" />
              ))}
            </div>
            {/* bars, sitting on the axis baseline */}
            <div className="flex flex-1 items-end justify-between gap-2 border-b border-[#F3F4F6]">
              {SKELETON_BAR_HEIGHTS.map((h, i) => (
                <div
                  key={i}
                  className="shimmer w-full rounded-t-md"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>
          {/* x-axis labels */}
          <div className="mt-2 flex gap-2 pl-7">
            {SKELETON_BAR_HEIGHTS.map((_, i) => (
              <div key={i} className="shimmer h-1.5 flex-1 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!data || data.length === 0) {
    return (
      <div>
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-[#111827]">{chartTitle}</h3>
          <p className="text-xs text-[#6B7280]">{chartSubtitle}</p>
        </div>
        <EmptyState />
      </div>
    );
  }

  // Sessions exist for the period but carry no load \u2014 show the zero-data state
  // instead of a flat, all-zero chart. Only in team mode: in comparison mode a
  // zero player line vs. non-zero peers is still worth plotting.
  const hasLoad = data.some((d) => Number(d[dataKey]) > 0);
  if (!comparisonMode && !hasLoad) {
    return (
      <div>
        <div className="mb-3">
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("team.loadChart.teamTitle")}
          </h3>
          <p className="text-xs text-[#6B7280]">
            {t("team.loadChart.teamSubtitle")}
          </p>
        </div>
        <ZeroLoadState count={data.length} />
      </div>
    );
  }

  return (
    <div>
      {/* Title */}
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[#111827]">{chartTitle}</h3>
        <p className="text-xs text-[#6B7280]">{chartSubtitle}</p>
      </div>

      {/* Legend */}
      <div className="mb-4 flex items-center justify-end gap-4">
        {comparisonMode ? (
          <>
            {sessionType !== "match" && (
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded"
                  style={{ backgroundColor: SESSION_TYPE_COLORS.training }}
                />
                <span className="text-xs text-[#6B7280]">
                  {t("team.sessionTypes.training")}
                </span>
              </div>
            )}
            {sessionType !== "training" && (
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 rounded"
                  style={{ backgroundColor: SESSION_TYPE_COLORS.match }}
                />
                <span className="text-xs text-[#6B7280]">
                  {t("team.sessionTypes.match")}
                </span>
              </div>
            )}
            {sessionType !== "match" && (
              <div className="flex items-center gap-1.5">
                <span
                  className="h-0.5 w-5 rounded-full bg-[#D97706] opacity-70"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg, #D97706 0, #D97706 4px, transparent 4px, transparent 7px)",
                  }}
                />
                <span className="text-xs text-[#6B7280]">
                  {t("team.loadChart.trainingPeerAvg")}
                </span>
              </div>
            )}
            {sessionType !== "training" && (
              <div className="flex items-center gap-1.5">
                <span
                  className="h-0.5 w-5 rounded-full bg-[#6366F1] opacity-70"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg, #6366F1 0, #6366F1 4px, transparent 4px, transparent 7px)",
                  }}
                />
                <span className="text-xs text-[#6B7280]">
                  {t("team.loadChart.matchPeerAvg")}
                </span>
              </div>
            )}
          </>
        ) : (
          sessionTypes.map((type) => {
            const isHidden = hiddenTypes.has(type);
            return (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className="flex items-center gap-1.5 transition-opacity duration-150"
                style={{ opacity: isHidden ? 0.4 : 1 }}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full transition-all duration-150"
                  style={{
                    backgroundColor: isHidden
                      ? "#D1D5DB"
                      : SESSION_TYPE_COLORS[type],
                  }}
                />
                <span
                  className="text-xs transition-all duration-150"
                  style={{
                    color: isHidden ? "#9CA3AF" : "#6B7280",
                    textDecoration: isHidden ? "line-through" : "none",
                  }}
                >
                  {typeLabel(type)}
                </span>
              </button>
            );
          })
        )}
      </div>

      {/* Chart */}
      <div className="h-[200px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={filteredData}
            margin={{
              top: 5,
              right: comparisonMode ? 70 : 5,
              left: -20,
              bottom: 5,
            }}
            barCategoryGap="20%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#F3F4F6"
            />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              dy={8}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: "#9CA3AF" }}
              tickFormatter={(value) => `${value}`}
              dx={-5}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F9FAFB" }} />
            {comparisonMode ? (
              <>
                {/* Training peer average line */}
                {sessionType !== "match" && calculatedPeerAvgs.training > 0 && (
                  <ReferenceLine
                    y={calculatedPeerAvgs.training}
                    stroke="#D97706"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    strokeOpacity={0.7}
                    label={{
                      value: t("team.sessionTypes.training"),
                      position: "right",
                      fill: "#D97706",
                      fontSize: 10,
                      fontWeight: 500,
                      dx: 4,
                    }}
                  />
                )}
                {/* Match peer average line - higher */}
                {sessionType !== "training" && calculatedPeerAvgs.match > 0 && (
                  <ReferenceLine
                    y={calculatedPeerAvgs.match}
                    stroke="#6366F1"
                    strokeDasharray="6 4"
                    strokeWidth={2}
                    strokeOpacity={0.7}
                    label={{
                      value: t("team.sessionTypes.match"),
                      position: "right",
                      fill: "#6366F1",
                      fontSize: 10,
                      fontWeight: 500,
                      dx: 4,
                    }}
                  />
                )}
                <Bar
                  dataKey={dataKey}
                  name={playerName}
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                >
                  {filteredData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={SESSION_TYPE_COLORS[entry.type] || "#E07A3A"}
                    />
                  ))}
                </Bar>
              </>
            ) : (
              <Bar
                dataKey={dataKey}
                name={t("team.loadChart.load")}
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              >
                {filteredData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={SESSION_TYPE_COLORS[entry.type] || "#E07A3A"}
                  />
                ))}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TeamLoadChart;
